import React from 'react';
import { Typography, Divider, Space, Image, Alert } from 'antd';
import { InfoCircleOutlined, CalculatorOutlined, CalendarOutlined, SearchOutlined, FilterOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const HelpMokykluRodikliuPildymas = () => (
    <div className="help-content">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
                <Title level={2} style={{ marginBottom: 16 }}>
                    Mokyklų rodiklių pildymas
                </Title>
                <Paragraph style={{ fontSize: 16, lineHeight: 1.8 }}>
                    Šiame skyriuje aprašytas mokyklų rodiklių duomenų suvedimo ir valdymo procesas VKSIS sistemoje.
                </Paragraph>
            </div>

            <div className="help-screenshot-placeholder">
                <Image
                    src="https://api.vksis.lt/img/help/mokyklu_rodikliu_pagalba_1.webp"
                    alt="Mokyklų rodiklių pildymo lango screenshot"
                    preview={false}
                    style={{ width: '100%', borderRadius: 8, border: '1px solid #e8e8e8' }} />
            </div>

            <Divider />

            <div>
                <Title level={3} style={{ marginBottom: 12 }}>
                    <InfoCircleOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                    Langas ir jo struktūra
                </Title>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    Rodiklių duomenų langą galite pasiekti iš mokyklos darbalaukio, pasirinkę tab&apos;ą <Text strong>&ldquo;Rodiklių duomenys&rdquo;</Text>. Lange matote:
                </Paragraph>
                <ul style={{ fontSize: 15, lineHeight: 1.8, marginTop: 12, marginBottom: 16 }}>
                    <li><Text strong>Kairėje pusėje</Text> – rodiklių kategorijų sąrašą (pvz., Pagrindiniai, Pasiekimų statistiniai, TŪM) su rodiklių skaičiumi kiekvienoje kategorijoje</li>
                    <li><Text strong>Viršuje</Text> – statistikos korteles, rodančias: Iš viso, Užpildyta, Neužpildyta, Skaičiuojami, Kritiniai rodikliai</li>
                    <li><Text strong>Filtravimo ir paieškos juostą</Text> su mygtukais: &ldquo;Nerandate rodiklių?&rdquo;, datos pasirinkimu, paieška, filtru ir atnaujinimo mygtuku</li>
                    <li><Text strong>Pagrindinėje srityje</Text> – rodiklių korteles su informacija apie kiekvieną rodiklį</li>
                </ul>
            </div>

            <Divider />

            <div>
                <Title level={3} style={{ marginBottom: 12 }}>
                    <ExclamationCircleOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                    Kritiniai rodikliai
                </Title>
                <Alert
                    message="Svarbu"
                    description={(
                        <Paragraph style={{ marginBottom: 0, fontSize: 14 }}>
                            <Text strong>Kritiniai rodikliai</Text> gali būti suvedami tik <Text strong>Švietimo skyriaus darbuotojų</Text>. Mokyklos vartotojai negali redaguoti kritinių rodiklių – jie matomi tik informaciniais tikslais.
                        </Paragraph>
                    )}
                    type="warning"
                    icon={<ExclamationCircleOutlined />}
                    showIcon
                    style={{ marginBottom: 16 }} />
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8 }}>
                    Kritiniai rodikliai pažymėti raudonu <Text strong>&ldquo;Kritinis&rdquo;</Text> ženkleliu ir yra paryškinti statistikos kortelėje. Šie rodikliai yra ypač svarbūs sistemos veikimui ir jų duomenys turi būti tikslūs.
                </Paragraph>
            </div>

            <Divider />

            <div>
                <Title level={3} style={{ marginBottom: 12 }}>
                    <CalculatorOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                    Skaičiuojami rodikliai
                </Title>
                <div className="help-screenshot-placeholder">
                    <Image
                        src="https://api.vksis.lt/img/help/mokyklu_rodikliu_pagalba_7.webp"
                        alt="Mokyklų rodiklių pildymo lango screenshot"
                        preview={false}
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e8e8e8' }} />
                </div>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    Kai kurie rodikliai yra <Text strong>skaičiuojami automatiškai</Text> pagal kitų rodiklių reikšmes. Tokie rodikliai pažymėti violetine <Text strong>&ldquo;Skaičiuojamas&rdquo;</Text> žyma su skaičiuoklės ikona.
                </Paragraph>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    Norint sužinoti, nuo kurių rodiklių priklauso skaičiuojamas rodiklis, spauskite <Text strong>&ldquo;Daugiau informacijos&rdquo;</Text> nuorodą rodiklio kortelėje. Atsidariusiame skyriuje matysite:
                </Paragraph>
                <ul style={{ fontSize: 15, lineHeight: 1.8, marginTop: 12, marginBottom: 16 }}>
                    <li><Text strong>Formulę</Text> – kaip rodiklis apskaičiuojamas</li>
                    <li><Text strong>Rodiklių sąrašą</Text> – nuo kurių rodiklių priklauso skaičiuojamas rodiklis</li>
                    <li><Text strong>Pastabą</Text> – kad rodiklis bus apskaičiuotas, kai mokykla pateiks visus reikalingus duomenis</li>
                </ul>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8 }}>
                    Skaičiuojami rodikliai negali būti redaguojami rankiniu būdu – jie apskaičiuojami automatiškai, kai užpildomi visi priklausomi rodikliai.
                </Paragraph>
            </div>

            <Divider />

            <div>
                <Title level={3} style={{ marginBottom: 12 }}>
                    <CalendarOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                    Duomenų suvedimo dažnumas ir laikotarpių pasirinkimas
                </Title>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    Rodikliai turi skirtingus duomenų suvedimo dažnumus:
                </Paragraph>
                <ul style={{ fontSize: 15, lineHeight: 1.8, marginTop: 12, marginBottom: 16 }}>
                    <li><Text strong>Mokslo metai</Text> – duomenys suvedami kartą per mokslo metus</li>
                    <li><Text strong>Pusmetis</Text> – duomenys suvedami kiekvieną mokslo metų pusmetį</li>
                    <li><Text strong>Ketvirtis</Text> – duomenys suvedami kas ketvirtį</li>
                    <li><Text strong>Kas mėnesį</Text> – duomenys suvedami kas mėnesį</li>
                    <li><Text strong>2 kartus per metus</Text> – duomenys suvedami dviem kartais per metus (pvz., liepos ir sausio mėnesiais, arba gegužės ir spalio mėnesiais)</li>
                </ul>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    <Text strong>Norint matyti ar redaguoti praeito laikotarpio reikšmes</Text>, reikia pakeisti kalendoriaus datą viršuje esančioje filtravimo juostoje. Data turi atitikti norimą laikotarpį.
                </Paragraph>

                <div className="help-screenshot-placeholder">
                    <Image
                        src="https://api.vksis.lt/img/help/mokyklu_rodikliu_pagalba_6.webp"
                        alt="Mokyklų rodiklių pildymo lango screenshot"
                        preview={false}
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e8e8e8' }} />
                </div>

                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    <Text strong>Pavyzdys:</Text> Jei norite gauti ir redaguoti 2024-2025 mokslo metų informaciją, pakeiskite datą į bet kurią tų mokslo metų datą, pvz., <Text strong>2025-09-01</Text> (2025 m. rugsėjo 1 d.), kuri atitiks tą laikotarpį.
                </Paragraph>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8 }}>
                    Pasirinkus datą, sistema automatiškai parodys visus rodiklius, susijusius su tuo laikotarpiu, ir jų reikšmes, jei jos buvo suvestos.
                </Paragraph>
            </div>

            <Divider />

            <div>
                <Title level={3} style={{ marginBottom: 12 }}>
                    <SearchOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                    Rodiklių paieška ir filtravimas
                </Title>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    Jei nerandate reikiamo rodiklio, naudokite šias funkcijas:
                </Paragraph>
                <ul style={{ fontSize: 15, lineHeight: 1.8, marginTop: 12, marginBottom: 16 }}>
                    <li><Text strong>&ldquo;Nerandate rodiklių?&rdquo;</Text> mygtukas – atidaro modalų langą su paieškos funkcija ir rodiklių sąrašu, kur galite rasti ir peržiūrėti visus prieinamus rodiklius</li>
                    <li><Text strong>Paieškos laukas</Text> – įveskite rodiklio pavadinimą arba kodą (pvz., R416) ir sistema filtruos rodiklius</li>
                    <li><Text strong>&ldquo;Filtras&rdquo;</Text> mygtukas – leidžia filtruoti rodiklius pagal kategorijas, statusą ir kitus kriterijus</li>
                    <li><Text strong>Kairėje pusėje esantis kategorijų sąrašas</Text> – pasirinkite konkrečią kategoriją, kad matytumėte tik tos kategorijos rodiklius</li>
                </ul>
                <div className="help-screenshot-placeholder">
                    <Image
                        src="https://api.vksis.lt/img/help/mokyklu_rodikliu_pagalba_5.webp"
                        alt="Mokyklų rodiklių pildymo lango screenshot"
                        preview={false}
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e8e8e8' }} />
                </div>
            </div>

            <Divider />

            <div>
                <Title level={3} style={{ marginBottom: 12 }}>
                    <InfoCircleOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                    Rodiklių kortelės ir jų informacija
                </Title>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    Kiekvienas rodiklis pateikiamas atskiroje kortelėje, kurioje matote:
                </Paragraph>
                <ul style={{ fontSize: 15, lineHeight: 1.8, marginTop: 12, marginBottom: 16 }}>
                    <li><Text strong>Rodiklio pavadinimą</Text> – pilnas rodiklio aprašymas</li>
                    <li><Text strong>Rodiklio kodą</Text> – unikalus rodiklio identifikatorius (pvz., R416) ir matavimo vienetas (vnt., %)</li>
                    <li><Text strong>Statusą</Text> – Užpildyta (žalia), Neužpildyta (oranžinė), Skaičiuojamas (violetinė), Kritinis (raudona)</li>
                    <li><Text strong>Laikotarpį</Text> – kokiam laikotarpiui priskirtas rodiklis (pvz., Pusmetis, I pusmetis 2025-2026 mokslo metais)</li>
                    <li><Text strong>Reikšmę</Text> – įvestą arba apskaičiuotą rodiklio reikšmę</li>
                    <li><Text strong>&ldquo;Daugiau informacijos&rdquo;</Text> nuorodą – išplečiamą sekciją su papildoma informacija apie rodiklį</li>
                </ul>
            </div>

            <Divider />

            <div>
                <Title level={3} style={{ marginBottom: 12 }}>
                    <FilterOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                    Rodiklių redagavimas
                </Title>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
                    Norint redaguoti rodiklių reikšmes, spauskite <Text strong>&ldquo;Redaguoti&rdquo;</Text> mygtuką viršuje dešinėje. Redagavimo režime:
                </Paragraph>
                <div className="help-screenshot-placeholder">
                    <Image
                        src="https://api.vksis.lt/img/help/mokyklu_rodikliu_pagalba_8.webp"
                        alt="Mokyklų rodiklių pildymo lango screenshot"
                        preview={false}
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e8e8e8' }} />
                </div>
                <div className="help-screenshot-placeholder">
                    <Image
                        src="https://api.vksis.lt/img/help/mokyklu_rodikliu_pagalba_2.webp"
                        alt="Mokyklų rodiklių pildymo lango screenshot"
                        preview={false}
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e8e8e8' }} />
                </div>
                <div className="help-screenshot-placeholder">
                    <Image
                        src="https://api.vksis.lt/img/help/mokyklu_rodikliu_pagalba_3.webp"
                        alt="Mokyklų rodiklių pildymo lango screenshot"
                        preview={false}
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e8e8e8' }} />
                </div>
                <div className="help-screenshot-placeholder">
                    <Image
                        src="https://api.vksis.lt/img/help/mokyklu_rodikliu_pagalba_4.webp"
                        alt="Mokyklų rodiklių pildymo lango screenshot"
                        preview={false}
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e8e8e8' }} />
                </div>

                <ul style={{ fontSize: 15, lineHeight: 1.8, marginTop: 12, marginBottom: 16 }}>
                    <li>Rodiklių kortelėse atsiras įvesties laukai, kuriuose galite įvesti arba pakeisti reikšmes</li>
                    <li>Viršuje matysite veiksmų mygtukus: <Text strong>&ldquo;Atmesti&rdquo;</Text>, <Text strong>&ldquo;Baigti redagavimą&rdquo;</Text> ir <Text strong>&ldquo;Išsaugoti&rdquo;</Text></li>
                    <li>Užpildyti rodikliai bus pažymėti geltona rėmeliu, rodančiu, kad reikšmė buvo pakeista</li>
                    <li>Išsaugojus pakeitimus, rodikliai bus atnaujinti ir jų statusas pasikeis į <Text strong>&ldquo;Užpildyta&rdquo;</Text>. Išsaugojus rodiklius, juos dar galite redaguoti</li>
                    <li>
                        Norint patvirtinti, kad informacija yra galutinė, pažymėkite <Text strong>&ldquo;Patvirtinti&rdquo;</Text> žymę rodiklio kortelėje ir išsaugokite.
                        Patvirtinus rodiklį, jo nebegalėsite redaguoti, o Švietimo skyrius žinos, kad baigėte su rodiklio redagavimu ir patvirtinate, kad informacija yra galutinė.
                    </li>
                </ul>
                <Paragraph style={{ fontSize: 15, lineHeight: 1.8 }}>
                    <Text strong>Pastabos:</Text>
                </Paragraph>
                <ul style={{ fontSize: 15, lineHeight: 1.8, marginTop: 8, marginBottom: 0 }}>
                    <li>Kritiniai rodikliai negali būti redaguojami mokyklos vartotojų – juos gali keisti tik Švietimo skyriaus darbuotojai.</li>
                    <li>Skaičiuojamiems rodikliams negalima nustatyti reikšmių rankiniu būdu – jų reikšmės bus automatiškai apskaičiuotos, kai bus įvestos visos reikiamos trūkstamos reikšmės.</li>
                </ul>
            </div>
        </Space>
    </div>
);

export default HelpMokykluRodikliuPildymas;
